import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
  useRef,
  useEffect,
} from "react";
import {
  cx,
  isValidUUID,
  priceDisplay,
  useResizeObserver,
  useTranslations,
} from "@jugl-web/utils";
import { format } from "date-fns";
import { DEFAULT_ENTITY_CURRENCY } from "@jugl-web/utils/consts";
import html2pdf from "html2pdf.js";
import { InvoiceModel } from "@jugl-web/rest-api";
import { caltItemPrice } from "../../utils";
import { usePrintInvoice } from "../../hooks/usePrintInvoice";

const DisplayPrice = ({
  price,
  currency,
}: {
  price: number;
  currency: string;
}) =>
  priceDisplay(price, currency, undefined, {
    className: "flex gap-1 items-center",
  });

interface InvoicePreviewProps {
  invoice: Partial<InvoiceModel>;
  hidden?: boolean;
  logo?: string;
  entityName?: string;
}

export interface InvoicePreviewHandle {
  download: () => void;
  print: () => void;
}

export const InvoicePreview = forwardRef<
  InvoicePreviewHandle,
  InvoicePreviewProps
>(({ invoice, logo, hidden, entityName }, ref) => {
  const { t } = useTranslations();
  const [minHeight, setMinHeight] = useState(0);
  const [blobUrl, setBlobUrl] = useState<string>("");
  const { ref: resizeRef } = useResizeObserver({
    onResize: ({ target: { clientWidth } }) => {
      setMinHeight(clientWidth * Math.sqrt(2));
    },
  });

  const { printInvoice } = usePrintInvoice();
  const printRef = useRef<HTMLDivElement>(null);

  const title = useMemo(() => {
    switch (invoice.type) {
      case "proforma":
        return "Proforma";
      case "estimate":
        return "Estimate";
      case "purchase_order":
        return "Purchase Order";
      default:
        return "Invoice";
    }
  }, [invoice.type]);

  useImperativeHandle(
    ref,
    () => ({
      download: async () => {
        if (!printRef.current) {
          return;
        }
        const pdfWorker = html2pdf()
          .from(printRef.current)
          .set({
            filename: `${
              [entityName, invoice.no].filter(Boolean).join("_") || title
            }.pdf`,
            image: { type: "jpeg", quality: 1 },
            html2canvas: { scale: 2, useCORS: true, letterRendering: true },
            margin: [15, 10, 15, 10],
            pagebreak: {
              // mode: ["avoid-all", "css", "legacy"],
              avoid: "tr",
              mode: "css",
            },
            jsPDF: {
              format: "a4",
              orientation: "portrait",
            },
            // oxlint-disable-next-line typescript/no-explicit-any
          } as any)
          .toPdf();

        const pdf = await pdfWorker.get("pdf");
        const totalPages = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i += 1) {
          pdf.setPage(i);
          pdf.setFontSize(10);
          pdf.setTextColor(0, 0, 0);
          pdf.setFontSize(8);
          pdf.text(
            `Page ${i}/${totalPages}`,
            pdf.internal.pageSize.getWidth() - 20,
            pdf.internal.pageSize.getHeight() - 10
          );
        }

        await pdfWorker.save();
      },
      print: () => {
        if (!printRef.current) {
          return;
        }
        printInvoice(printRef.current);
      },
    }),
    [entityName, invoice.no, title, printInvoice]
  );

  const fromLines = invoice.bill_from
    ? Object.keys(invoice.bill_from || {}).map(
        (key) => invoice.bill_from?.[key]
      )
    : undefined;
  const toLines = invoice.bill_to
    ? Object.keys(invoice.bill_to || {})
        .filter((key) => !isValidUUID(invoice.bill_to?.[key] || ""))
        .map((key) => invoice.bill_to?.[key])
    : undefined;

  let { currency = DEFAULT_ENTITY_CURRENCY } = invoice;
  if (invoice.currency) {
    currency = invoice.currency.toUpperCase();
  }
  const issueDate = invoice.date
    ? format(new Date(invoice.date), "dd MMM yyyy")
    : undefined;
  const dueDate = invoice.due_on
    ? format(new Date(invoice.due_on), "dd MMM yyyy")
    : undefined;

  const totalAfterDiscount = useMemo(() => {
    if (!invoice.total || !invoice.discount) return invoice.total || 0;
    return (
      invoice.total -
      (invoice.discount.reduce(
        (acc, curr) =>
          acc + curr.percentage
            ? (invoice.total || 0) * (curr.percentage / 100)
            : curr.value || 0,
        0
      ) || 0)
    );
  }, [invoice.discount, invoice.total]);
  const logoSrc = logo || invoice.logo;

  useEffect(() => {
    const fetchImageAsBlob = async () => {
      if (!logoSrc) {
        setBlobUrl("");
        return;
      }

      try {
        const response = await fetch(logoSrc);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
      } catch (error) {
        // oxlint-disable-next-line no-console
        console.error("Failed to fetch image as blob:", error);
        setBlobUrl(logoSrc);
      }
    };

    fetchImageAsBlob();

    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
    // oxlint-disable-next-line react/exhaustive-deps
  }, [logoSrc]);

  const taxesList = useMemo(
    () =>
      invoice.tax?.map((tax) => ({
        name: tax.name,
        price: (totalAfterDiscount || 0) * (tax.percentage / 100) * 100,
        percentage: tax.percentage,
      })),
    [invoice.tax, totalAfterDiscount]
  );

  const totalTaxPrice = useMemo(
    () =>
      taxesList?.reduce((acc, curr) => acc + Math.round(curr.price), 0) || 0,
    [taxesList]
  );

  return (
    <div
      ref={resizeRef}
      className={cx("relative", {
        "fixed -top-[9999px] -left-[9999px]": hidden,
      })}
      style={{ minHeight }}
    >
      <div ref={printRef} className="bg-white pb-10">
        <div className="font-secondary text-sm">
          <div className="flex items-start justify-between">
            {blobUrl ? (
              <img
                src={blobUrl}
                alt="Logo"
                className="w-24 rounded-full 2xl:w-36"
              />
            ) : (
              <div />
            )}

            <div className="text-right">
              <h1 className="mt-0 mb-5 text-4xl font-bold text-dark">
                {title}
              </h1>
              {fromLines && fromLines.length > 0 && (
                <ul className="space-y-1 pl-0 text-dark-800 [&>li]:list-none">
                  {fromLines.map((value, idx) => (
                    <li key={+idx}>{value}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="mt-12 flex justify-between">
            <div>
              <h2 className="mt-0 mb-3.5 font-secondary text-xl text-dark">
                Bill to
              </h2>

              {toLines && toLines.length > 0 && (
                <ul className="my-0 space-y-2 pl-0 text-dark-800 [&>li]:list-none">
                  {toLines.map((value, idx) => (
                    <li key={+idx}>{value}</li>
                  ))}
                </ul>
              )}
            </div>
            <ul className="my-0 flex min-w-[16rem] flex-col justify-end gap-2.5 text-dark-800 [&>li]:list-none">
              {invoice.no && (
                <li className="flex justify-between gap-4">
                  <span className="font-medium">
                    {t({
                      id: "invoices.item.invoiceNumber",
                      defaultMessage: "Invoice Number",
                    })}
                    :
                  </span>
                  <span className="font-normal">{invoice.no}</span>
                </li>
              )}
              {issueDate && (
                <li className="flex justify-between gap-4">
                  <span className="font-medium">
                    {t({
                      id: "invoices.item.issueDate",
                      defaultMessage: "Issue Date",
                    })}
                    :
                  </span>
                  <span className="font-normal">{issueDate}</span>
                </li>
              )}
              {dueDate && (
                <li className="flex justify-between gap-4">
                  <span className="font-medium">
                    {t({
                      id: "invoices.item.dueDate",
                      defaultMessage: "Due Date",
                    })}
                    :
                  </span>
                  <span className="font-normal">{dueDate}</span>
                </li>
              )}
            </ul>
          </div>

          <div className="mt-12">
            <div className="overflow-hidden rounded-t-2xl">
              <table className="w-full border-collapse text-left text-xs font-normal">
                <thead className="bg-dark">
                  <tr className="text-xs text-white">
                    <th className="px-4 py-2 font-normal">
                      {t({
                        id: "invoices.item.itemServiceName",
                        defaultMessage: "Item/Service Name",
                      })}
                    </th>
                    <th className="px-4 py-2 font-normal whitespace-nowrap">
                      {t({
                        id: "invoices.item.priceUnit",
                        defaultMessage: "Price (unit)",
                      })}
                    </th>
                    <th className="px-4 py-2 font-normal">
                      {t({
                        id: "invoices.item.quantity",
                        defaultMessage: "Qty.",
                      })}
                    </th>
                    <th className="px-4 py-2 font-normal">
                      {t({
                        id: "invoices.item.amount",
                        defaultMessage: "Amount",
                      })}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white text-gray-800">
                  {invoice.items
                    ?.filter((item) => item.name)
                    ?.map((item, idx) => {
                      const price = caltItemPrice({
                        price: item.price * 100,
                        qty: item.qty,
                      });

                      return (
                        <tr
                          key={`${item.name}-${+idx}`}
                          className={cx(
                            invoice.items?.length &&
                              idx < invoice.items.length - 1
                              ? "border-t-0 border-r-0 border-b border-l-0 border-solid border-grey-200"
                              : "",
                            "break-before-avoid break-inside-avoid"
                          )}
                        >
                          <td
                            className="w-2/5 max-w-sm px-4 py-2"
                            style={{
                              wordBreak: "break-word",
                            }}
                          >
                            {item.name}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            <DisplayPrice
                              price={item.price * 100}
                              currency={currency}
                            />
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            {item.qty}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            <DisplayPrice price={price} currency={currency} />
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>

              <div className="my-4 h-px w-full bg-dark-500" />
            </div>
          </div>

          <div className="flex w-full text-dark-800">
            <div className="flex-1 pl-4">
              {invoice.notes && (
                <div>
                  <h3 className="mb-1 text-sm">
                    {t({
                      id: "invoices.item.additionalNotes",
                      defaultMessage: "Additional notes",
                    })}
                  </h3>
                  <p className="text-xs whitespace-pre-wrap">{invoice.notes}</p>
                </div>
              )}
            </div>
            <div>
              <ul className="min-w-[22rem] space-y-2.5 px-4">
                <li className="flex justify-between gap-4">
                  <span>
                    {t({
                      id: "invoices.item.subtotal",
                      defaultMessage: "Subtotal",
                    })}
                  </span>
                  <div className="w-28">
                    {invoice.total ? (
                      <DisplayPrice
                        price={invoice.total * 100}
                        currency={currency}
                      />
                    ) : (
                      "-"
                    )}
                  </div>
                </li>

                {invoice.discount
                  ?.filter((item) => item.value || item.percentage)
                  ?.map((discount, index) => (
                    <li key={index} className="flex justify-between gap-4">
                      {/* TODO: name? */}
                      <div>
                        <span>
                          {t({
                            id: "invoices.item.invoiceDiscount",
                            defaultMessage: "Invoice discount",
                          })}
                        </span>
                        {discount.percentage ? (
                          <span className="pl-2 text-grey-700">
                            {discount.percentage}%
                          </span>
                        ) : null}
                      </div>
                      <div className="flex w-28 items-center gap-1">
                        {discount.value || discount.percentage ? "-" : ""}
                        {discount.percentage ? (
                          <DisplayPrice
                            price={
                              (invoice.total || 0) *
                              (discount.percentage / 100) *
                              100
                            }
                            currency={currency}
                          />
                        ) : (
                          <DisplayPrice
                            price={discount.value * 100}
                            currency={currency}
                          />
                        )}
                      </div>
                    </li>
                  ))}

                {taxesList?.map((tax) => (
                  <li key={tax.name} className="flex justify-between gap-4">
                    <div>
                      <span>{tax.name}</span>
                      <span className="pl-2 text-grey-700">
                        {tax.percentage}%
                      </span>
                    </div>
                    <div className="w-28">
                      <DisplayPrice price={tax.price} currency={currency} />
                    </div>
                  </li>
                ))}

                <li className="flex justify-between gap-4">
                  <span>
                    {t({
                      id: "invoices.item.tax-total",
                      defaultMessage: "Invoice tax",
                    })}
                  </span>
                  <div className="w-28">
                    <DisplayPrice price={totalTaxPrice} currency={currency} />
                  </div>
                </li>
              </ul>

              <div className="my-3.5 h-px w-full bg-dark-500" />

              <strong className="flex justify-between gap-4 px-4">
                {t({
                  id: "invoices.item.totalAmountDue",
                  defaultMessage: "Total amount due",
                })}
                <div className="w-28">
                  {invoice.grand_total ? (
                    <DisplayPrice
                      price={invoice.grand_total * 100}
                      currency={currency}
                    />
                  ) : (
                    "-"
                  )}
                </div>
              </strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

InvoicePreview.displayName = "InvoicePreview";
